import { Collection } from '@nozbe/watermelondb'
import { mapKeys } from 'lodash'
import database from 'model'
import Logger from 'services/Logger'
import { EventDoc, GroupDoc } from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { extract, omit } from 'utils/object'

class Database {
  getCollection<T>(collectionType: CollectionType): Collection<Model<T>> {
    return database.get(collectionType)
  }

  async getById(collectionType: CollectionType, id: string) {
    return database.get(collectionType).find(id)
  }

  async clearCollection(collectionType: CollectionType): Promise<void> {
    const collection = database.get(collectionType)
    const allRecords = await collection.query().fetch()
    await database.write(async () => {
      await database.batch(
        ...allRecords.map((record) => record.prepareDestroyPermanently())
      )
    })
  }

  async clear() {
    try {
      const collections = database.collections
      const tableNames = Object.keys(collections.map)

      // Fetch all records first (outside the write transaction)
      const allRecordsToDelete: Model<any>[] = []
      for (const tableName of tableNames) {
        const collection = collections.get(tableName)
        const records = await collection.query().fetch()
        allRecordsToDelete.push(...records)
      }

      // Then delete everything in a single batch operation
      if (allRecordsToDelete.length > 0) {
        await database.write(async () => {
          await database.batch(
            ...allRecordsToDelete.map((record) =>
              record.prepareDestroyPermanently()
            )
          )
        })
      }
      Logger.debug('Database', 'All collections cleared successfully')
    } catch (error) {
      Logger.red('Database', 'Error clearing database', error)
      throw error
    }
  }

  async unsafeReset() {
    try {
      await database.write(async () => {
        await database.unsafeResetDatabase()
      })
    } catch (error) {
      Logger.red('Database', 'Error resetting database', error)
      throw error
    }
  }

  async batch(
    ...e: (false | void | Model<any> | Promise<void> | null)[]
  ): Promise<void> {
    return database.batch(...e)
  }

  async write<T>(cb: () => Promise<T>) {
    return await database.write<T>(cb)
  }

  updateFieldsInLocalDB<T>(existing: Model<T>, dataFromServer: any) {
    return this.write(async () => {
      if (existing && existing.syncStatus != 'deleted') {
        existing.update(() => {
          Object.keys(dataFromServer).forEach((key) => {
            if (['_sortOrder'].includes(key)) {
              ;(existing as any)._raw[key] = dataFromServer[key]
            } else {
              ;(existing as any)[key] = dataFromServer[key]
            }
          })
        })
      }
    })
  }

  toServerObject<T>(data?: Model<T> | EventDoc | GroupDoc | null) {
    if (!data) return {}
    if (!Object.keys(data).includes('_raw')) return data
    return omit(extract(data, Object.keys(data._raw)), [
      'id',
      '_status',
      '_changed',
      '_message',
      'LastUpdateMessage',
      '_sortOrder'
    ])
  }

  toCreateObject<T>(data: Model<T>) {
    return omit(extract(data, Object.keys(data._raw)), [
      'id',
      '_status',
      '_changed',
      '_message',
      'LastUpdateMessage',
      '_rev',
      '_id',
      'AggregatedParticipantsConfirmationStatus',
      'AggregatedParticipantsDeliveryStatus'
    ])
  }
}

export default new Database()
